/**
 * SubmissionTracker — Phase 1
 *
 * Persistent strip across the top of participant and facilitator views showing
 * "X of Y submitted" with an avatar stack and progress ring. Updates in
 * real time as tool_session_responses are inserted.
 *
 * Pass toolSessionId and participantIds from the session context.
 * When no toolSessionId is active the strip renders nothing.
 */

import { useMemo } from 'react';
import { mergeParticipantRoster } from '../../../lib/sessionParticipantRoster';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { usePresenceStore } from '../../../stores/prioritization/presenceStore';
import { SubmissionDonut } from './layout/SubmissionDonut';
import { C } from '../theme/participantTokens';

interface SubmissionTrackerProps {
  toolSessionId: string | null;
  participantIds: string[];
  /** S-3: names are hidden in this session — show counts, no names. */
  hideNames?: boolean;
}

function trackerInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

export function SubmissionTracker({ toolSessionId, participantIds, hideNames = false }: SubmissionTrackerProps) {
  const participantResponses = useToolSessionStore(s => s.participantResponses);
  const presentUsers = usePresenceStore(s => s.presentUsers);

  const submittedResponses = useMemo(
    () => toolSessionId
      ? participantResponses.filter(r => r.toolSessionId === toolSessionId && r.isComplete)
      : [],
    [participantResponses, toolSessionId]
  );

  const submittedIds = useMemo(() => new Set(submittedResponses.map(r => r.participantId)), [submittedResponses]);
  const rosterIds = useMemo(
    () => mergeParticipantRoster(participantIds, submittedResponses.map((r) => r.participantId)),
    [participantIds, submittedResponses],
  );
  const total = rosterIds.length;
  const submitted = submittedResponses.length;

  if (!toolSessionId || total === 0) return null;

  const allDone = submitted >= total;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '6px 16px',
        background: allDone ? 'rgba(52,211,153,0.06)' : C.surface,
        borderBottom: `1px solid ${allDone ? 'rgba(52,211,153,0.2)' : C.rule}`,
        flexShrink: 0,
      }}
    >
      <SubmissionDonut submitted={submitted} total={total} color={allDone ? '#34d399' : 'var(--mustard)'} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '0.62rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: allDone ? '#34d399' : C.textTer,
          }}
        >
          {allDone ? 'All submitted' : `${submitted} of ${total} submitted`}
        </div>

        {/* Avatar stack */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 4 }}>
          {rosterIds.map(pid => {
            const user = presentUsers.find(u => u.userId === pid);
            const response = submittedResponses.find((r) => r.participantId === pid);
            const name = user?.userName ?? response?.participantName ?? pid.slice(0, 8);
            const done = submittedIds.has(pid);
            const online = user?.isOnline ?? false;
            return (
              <div
                key={pid}
                title={hideNames ? (done ? 'Submitted' : 'Not yet') : `${name}${done ? ' · submitted' : ''}${!online ? ' · offline' : ''}`}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: done ? '#34d399' : online ? C.slate : C.textDim,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.4rem',
                  fontWeight: 700,
                  color: C.bgRaised,
                  border: `1.5px solid ${C.bgRaised}`,
                  marginLeft: -4,
                  opacity: done || online ? 1 : 0.4,
                }}
              >
                {done ? '✓' : hideNames ? '' : trackerInitials(name)}
              </div>
            );
          })}
        </div>
      </div>

      {allDone && (
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '0.58rem',
            color: '#34d399',
            letterSpacing: '0.08em',
          }}
        >
          Ready to reveal →
        </span>
      )}
    </div>
  );
}
